import {Mapper} from '@s-ui/domain'

export class SeoNewsListMetaInformationMapper extends Mapper {
  constructor({config, seoDataValueObjectFactory, seoDataMapper, seoLiteralMapper}) {
    super()

    this._config = config
    this._seoDataValueObjectFactory = seoDataValueObjectFactory
    this._seoDataMapper = seoDataMapper
    this._seoLiteralMapper = seoLiteralMapper
  }

  /**
   * Capitalize first letter ignoring accents
   * @method
   */
  _capitalize = text => {
    return text ? this._replaceToSpace(text).replace(/(^|\s)\S/g, ltr => ltr.toUpperCase()) : text
  }

  /**
   * replace - and _ to blank space
   * @method
   */
  _replaceToSpace = text => text.replace(/-|_/g, ' ')

  /**
   * Replace pagination pattern to page or returns nothing
   * @method
   */
  _paginationReplacement = (match, page) => (page > 1 ? match.replace(/%{page}/g, page) : '')

  /**
   * News search page doesn't have canonical url, so if we are in news search page we return null.
   * else we  return the dynamic path of canonical url
   * @method
   */
  _parseCanonicalPath = ({text, term = '', page = 1, pageName}) => {
    const {NEWS_SEARCH_PAGE_NAME, PARAMS_PATTERN, PAGINATION_PATTERN} = this._config.get('page')
    const PAGINATION_PATTERN_REGEX = new RegExp(`${PARAMS_PATTERN}${PAGINATION_PATTERN}%{page}`, 'g')
    return pageName !== NEWS_SEARCH_PAGE_NAME
      ? text
          .replace(/%{tag}/g, term)
          .replace(PAGINATION_PATTERN_REGEX, match => this._paginationReplacement(match, page))
          .toLowerCase()
      : null
  }

  /**
   * Build canonical Url checking if it exists the dynamic path
   * @method
   */
  _buildCanonicalUrl = dynamicPath => {
    const COMPANY_URL = this._config.get('COMPANY_URL')
    return dynamicPath ? `${COMPANY_URL}${dynamicPath}` : ''
  }

  _buildRobots = ({nResults, metaRobots}) => {
    const {NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS} = this._config.get('seo')

    return nResults ? metaRobots : NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS
  }

  async map({culture, params = {}, pageName}) {
    const {term, category = 'portada', page, nResults} = params

    const {news: newsConfig} = this._config.get('seo')

    const {NEWS_CATEGORY_PAGE_NAME} = this._config.get('page')

    const newsPageConfig =
      pageName === NEWS_CATEGORY_PAGE_NAME ? newsConfig[pageName][category][culture] : newsConfig[pageName][culture]

    const {ALTERNATE, TITLE, META_DESCRIPTION, META_ROBOTS, CANONICAL, H1} = newsPageConfig

    const placeholderValues = {
      tag: this._capitalize(term),
      searchTerm: this._capitalize(term)
    }

    const parsedH1 = this._seoLiteralMapper.setText({text: H1}).setPlaceholderValues({placeholderValues}).map()

    const parsedTitle = this._seoLiteralMapper.setText({text: TITLE}).setPlaceholderValues({placeholderValues}).map()

    const parsedDescription = this._seoLiteralMapper
      .setText({text: META_DESCRIPTION})
      .setPlaceholderValues({placeholderValues})
      .map()

    const {links, metas, scripts, title} = this._seoDataMapper
      .title(parsedTitle)
      .description(parsedDescription)
      .robots(this._buildRobots({nResults, metaRobots: META_ROBOTS}))
      .alternate(ALTERNATE)
      .canonical(this._buildCanonicalUrl(this._parseCanonicalPath({text: CANONICAL, term, page, pageName})))
      .setOpenGraphData({
        title: parsedTitle,
        description: parsedDescription,
        url: this._buildCanonicalUrl(this._parseCanonicalPath({text: CANONICAL, term, page, pageName}))
      })
      .setTwitterCardsData()
      .map()

    return this._seoDataValueObjectFactory({
      h1: parsedH1,
      head: {
        link: links,
        meta: metas,
        scripts,
        title
      }
    })
  }
}
